import { useCallback, useEffect, useState } from "react";

import { isAppOnLatestVersion } from "@/helpers/app-version";
import { useTranslation } from "@/translations";

import { useFlashToast } from "../FlashToast/FlashToast";

// Responsible for prompting the user to update the app version if they are not on the latest version.
export function AppVersionUpdateManager(): React.ReactNode {
  const [isPrompted, setIsPrompted] = useState(false);

  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  const validateCurrentAppVersion = useCallback(() => {
    if (isPrompted || process.env.NODE_ENV === "development") {
      return;
    }

    isAppOnLatestVersion()
      .then((isLatest) => {
        if (isLatest) return;

        setTimeout(() => {
          showFlashToast({
            variant: "neutral",
            message: t("component.update-available.notification.title"),
            description: t("component.update-available.notification.description"),
            shouldAutoClose: false,
            action: {
              label: "Reload",
              onClick: () => window.location.reload(),
            },
          });

          setIsPrompted(true);
        }, 3000);
      })
      .catch((error) => {
        console.error("Failed to check app version:", error);
      });
  }, [isPrompted, showFlashToast, t]);

  useEffect(() => {
    window.addEventListener("focus", validateCurrentAppVersion);

    return () => {
      window.removeEventListener("focus", validateCurrentAppVersion);
    };
  }, [validateCurrentAppVersion]);

  return null;
}
